import styled from "@emotion/styled";
import { usePermissions } from "../../../hooks/usePermissions";
import { LoadingScreen } from "../../shared/LoadingScreen";
import { PermissionDenied } from "../../shared/PermissionDenied";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Modal,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  getWithQueryApiServices,
  postWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { companyFormInitVal } from "../../../constants";
import { SplitContainer, StyledHeader } from "../../../styles/commonStyles";
import { IconColor } from "../../../styles/constants";
import { errorToast, successToast } from "../../../utils/helper";
import { FullFeaturedCrudGrid } from "../../shared/CommonTable";
import { ParentPageWrapper } from "../../shared/ParentPageWrapper";
import PaymentForm from "../../shared/PaymentForm";
import { InvoiceForm } from "./InvoiceForm";
import { PaymentMethodPanel } from "./PaymentMethodPanel";

const BoxContainer = styled(Box)(({ theme }) => ({
  padding: "10px",
  [theme.breakpoints.up("sm")]: {
    padding: "24px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "1%",
  },
  boxSizing: "border-box",
}));

const ParentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "stretch",
  },
  width: "100%",
}));

const ActionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  height: "100%",
}));

const formatUsd = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return `$${n.toLocaleString()}`;
};

const parseGridDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const gatewayLabel = (status) => {
  switch (status) {
    case "success":
      return { label: "Success", color: "success" };
    case "failed":
      return { label: "Failed", color: "error" };
    case "no_method":
      return { label: "No payment method", color: "warning" };
    case "pending":
      return { label: "Pending", color: "warning" };
    default:
      return { label: status || "—", color: "default" };
  }
};

export const Invoice = () => {
  const outletContext = useOutletContext();
  const {
    currentCompany,
    fullCompanyList,
    isCompanySwitching,
    tenantString,
    setCurrentCompany,
  } = outletContext;

  const { hasPermission, isGlobalSuperAdmin } = usePermissions();
  const canReadInvoice =
    isGlobalSuperAdmin || hasPermission("Invoices & Billing: Read");
  const canEditInvoice = isGlobalSuperAdmin;
  // Tenants (not global SA) can manage payment method
  const canManagePaymentMethod = !isGlobalSuperAdmin && canReadInvoice;

  const [searchParams, setSearchParams] = useSearchParams();
  const failedOnly = searchParams.get("failed") === "1";

  const handleGlobalCompanyChange = (e) => {
    if (setCurrentCompany) setCurrentCompany(e.target.value);
  };

  const queryClient = useQueryClient();
  const [companyForm, setCompanyForm] = useState([]);
  const [searchCategory, setSearchCategory] = useState("");
  const [debouncedSearchCategory, setDebouncedSearchCategory] = useState("");
  const [open, setOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryData, setCategoryData] = useState({});
  const [addNewCategory, setAddNewCategory] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchCategory(searchCategory);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchCategory]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [currentCompany, searchCategory]);

  useEffect(() => {
    if (!fullCompanyList?.length || !setCurrentCompany) return;
    const allowedIds = fullCompanyList.map((c) => Number(c.companyID));
    if (currentCompany && allowedIds.includes(Number(currentCompany))) return;
    setCurrentCompany(fullCompanyList[0].companyID);
  }, [fullCompanyList, currentCompany, setCurrentCompany]);

  const updateCategory = useMutation({
    mutationKey: ["payInvoiceform"],
    mutationFn: ({ invoiceID, formData, companyID }) =>
      postWithQueryApiServices(
        `${apiRoutes.PAY_INVOICE}/${invoiceID}`,
        // Pay against the invoice's own company, which must be in the user's scope.
        `selectedCompany=${companyID || currentCompany || ""}${tenantString || ""}`,
        formData
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoiceLists"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceAlerts"] });
      setAddNewCategory(false);
      setOpen(false);
      successToast("Invoice paid successfully");
    },
    onError: (err) => {
      errorToast(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Payment failed. Please try again."
      );
    },
  });

  const columns = useMemo(
    () => [
      {
        field: "invoiceNumber",
        headerName: "Invoice #",
        flex: 0.7,
        minWidth: 130,
        editable: false,
      },
      {
        field: "companyID",
        headerName: "Invoiced to Company",
        flex: 1,
        editable: false,
        valueGetter: (params) => {
          return fullCompanyList?.find((_) => _.companyID === params)?.name;
        },
      },
      {
        field: "amount",
        headerName: "Invoice amount",
        align: "left",
        headerAlign: "left",
        editable: false,
        flex: 0.5,
        type: "number",
        valueFormatter: (value) => formatUsd(value),
      },
      {
        field: "invoicedate",
        headerName: "Invoice Date",
        align: "left",
        headerAlign: "left",
        editable: false,
        flex: 0.6,
        type: "date",
        valueGetter: (params) => parseGridDate(params),
      },
      {
        field: "status",
        align: "center",
        headerName: "Status",
        headerAlign: "center",
        flex: 0.3,
        renderCell: (params) => {
          const gateway = params.row?.gatewayStatus;
          if (params.value === "paid" || params.value === "trial") {
            return (
              <ActionContainer>
                <CheckIcon color="success" sx={{ fontSize: 20 }} />
              </ActionContainer>
            );
          }
          // Unpaid: distinguish pending auto-charge vs hard failure vs unpaid
          if (gateway === "pending" || gateway === "no_method") {
            return (
              <ActionContainer>
                <HourglassEmptyIcon
                  color="warning"
                  sx={{ fontSize: 20 }}
                  titleAccess={
                    gateway === "no_method"
                      ? "Awaiting payment method"
                      : "Payment pending"
                  }
                />
              </ActionContainer>
            );
          }
          return (
            <ActionContainer>
              <CloseIcon
                color="error"
                sx={{ fontSize: 20 }}
                titleAccess={
                  gateway === "failed" ? "Payment failed" : "Unpaid"
                }
              />
            </ActionContainer>
          );
        },
      },
      {
        field: "gatewayStatus",
        headerName: "Payment gateway",
        flex: 0.7,
        align: "center",
        headerAlign: "center",
        editable: false,
        renderCell: (params) => {
          const meta = gatewayLabel(params.value);
          return (
            <Chip
              size="small"
              label={meta.label}
              color={meta.color}
              variant={params.value === "failed" ? "filled" : "outlined"}
              title={params.row?.gatewayMessage || ""}
            />
          );
        },
      },
      {
        align: "center",
        headerAlign: "center",
        flex: 0.4,
        field: "payOrView",
        headerName: "",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (params?.row?.status === "unpaid") {
            return (
              <>
                <Button
                  variant="contained"
                  onClick={() => setOpen(params.row.invoiceID)}
                >
                  Pay
                </Button>
                <Modal
                  open={open === params.row.invoiceID}
                  aria-labelledby="payment-modal"
                  aria-describedby="payment-modal-description"
                  onClose={() => setOpen(false)}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: 400,
                      bgcolor: "background.paper",
                      boxShadow: 24,
                      p: 4,
                    }}
                  >
                    <h2 id="payment-modal">Payment</h2>
                    <PaymentForm
                      invoiceID={params.row.invoiceID}
                      isSubmitting={updateCategory.isPending}
                      onSubmit={(arg) =>
                        updateCategory.mutate({
                          ...arg,
                          companyID: params.row.companyID,
                        })
                      }
                    />
                  </Box>
                </Modal>
              </>
            );
          }
          return (
            <IconButton
              onClick={() => handleCategoryCheckbox(params.row)}
              aria-label="view invoice"
              sx={{ p: 0.5 }}
            >
              <VisibilityIcon sx={{ color: IconColor, fontSize: 20 }} />
            </IconButton>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fullCompanyList, open, updateCategory.isPending]
  );

  const handleCategoryCheckbox = (item) => {
    let copyValue = [...selectedCategories];

    if (copyValue.some((cat) => cat?.invoiceID === item?.invoiceID)) {
      copyValue = copyValue.filter((val) => val?.invoiceID !== item?.invoiceID);
    } else {
      copyValue = [item];
    }

    setSelectedCategories([...copyValue]);

    if (copyValue?.length === 1) {
      setCategoryData(copyValue?.[0]);
      setAddNewCategory(true);
    } else {
      setCategoryData({});
      setAddNewCategory(false);
    }
  };

  // Same company-scoped list as Categories: invoices for the selected company only.
  const companyQuery = currentCompany
    ? `&selectedCompany=${currentCompany}`
    : "";

  const listEnabled = !!currentCompany;

  const { data: invoiceData, isLoading: isInvoiceLoading, refetch: categoryListRefetch } =
    useQuery({
      queryKey: [
        "invoiceLists",
        currentCompany,
        debouncedSearchCategory,
        tenantString,
        paginationModel,
        failedOnly,
      ],
      queryFn: () =>
        getWithQueryApiServices(
          apiRoutes.GET_INVOICE_LIST,
          `pageNumber=${paginationModel.page + 1}&pageSize=${paginationModel.pageSize}${companyQuery}&search=${debouncedSearchCategory}${failedOnly ? "&failedOnly=1" : ""}${tenantString || ""}`
        ),
      enabled: listEnabled,
      placeholderData: keepPreviousData,
    });

  const handleAddNew = () => {
    setCategoryData({});
    setAddNewCategory(true);
  };

  useEffect(() => {
    if (currentCompany) {
      setAddNewCategory(false);
      setSelectedCategories([]);
      setCompanyForm([]);
      setSearchCategory("");
      setCategoryData({});
    }
  }, [currentCompany]);

  useEffect(() => {
    if (categoryData?.invoiceID && invoiceData?.data?.list) {
      const updatedInvoice = invoiceData.data.list.find(
        (inv) => inv.invoiceID === categoryData.invoiceID
      );
      if (
        updatedInvoice &&
        JSON.stringify(updatedInvoice) !== JSON.stringify(categoryData)
      ) {
        setCategoryData(updatedInvoice);
        setSelectedCategories([updatedInvoice]);
      }
    }
  }, [invoiceData, categoryData?.invoiceID]);

  // When arriving from failure banner, select first failed row
  useEffect(() => {
    if (failedOnly && invoiceData?.data?.list?.length) {
      const first = invoiceData.data.list[0];
      if (first) {
        setCategoryData(first);
        setSelectedCategories([first]);
        setAddNewCategory(true);
      }
    }
  }, [failedOnly, invoiceData?.data?.list]);

  const getRowClassName = (params) =>
    params?.row?.gatewayStatus === "failed" ? "invoice-row-failed" : "";

  if (isInvoiceLoading && listEnabled && !isCompanySwitching) {
    return (
      <ParentPageWrapper>
        <BoxContainer>
          <LoadingScreen message="Loading invoices..." />
        </BoxContainer>
      </ParentPageWrapper>
    );
  }

  if (!canReadInvoice) {
    return (
      <ParentPageWrapper>
        <PermissionDenied message="You do not have permission to view or manage Invoices. Please contact your administrator." />
      </ParentPageWrapper>
    );
  }

  return (
    <ParentPageWrapper>
      <BoxContainer>
        <StyledHeader>Invoices</StyledHeader>

        <PaymentMethodPanel canManage={canManagePaymentMethod} />

        {failedOnly && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip color="error" label="Showing failed payments" />
            <Button
              size="small"
              onClick={() => {
                searchParams.delete("failed");
                setSearchParams(searchParams);
              }}
            >
              Clear filter
            </Button>
          </Box>
        )}

        <ParentContainer>
          <SplitContainer
            style={{
              backgroundColor: "transparent",
              boxShadow: "none",
            }}
            sx={{
              width: { xs: "100%", md: "60%" },
            }}
          >
            <Box id="tour-invoice-grid" sx={{ height: "80vh" }}>
              <Box
                sx={{
                  backgroundColor: "white",
                  height: { xs: "auto", md: "92%" },
                  borderRadius: 4,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  "& .invoice-row-failed": {
                    backgroundColor: "rgba(211, 47, 47, 0.08) !important",
                  },
                }}
              >
                <Box sx={{ p: 2, pb: 1, borderBottom: "1px solid #edf2f7" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#1a365d", fontSize: "14px" }}>
                    Select Company
                  </Typography>
                  <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                    <Select
                      value={currentCompany || ""}
                      onChange={handleGlobalCompanyChange}
                      sx={{
                        borderRadius: "6px",
                        backgroundColor: "#f8fafc",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#cbd5e1",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#94a3b8",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Company
                      </MenuItem>
                      {fullCompanyList?.filter((company) => Number(company.dst) !== 0).map((company) => (
                        <MenuItem key={company.companyID} value={company.companyID}>
                          {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    padding: "0 8px 8px 8px",
                  }}
                >
                  <FullFeaturedCrudGrid
                    initialRows={invoiceData?.data?.list}
                    columns={columns}
                    label="invoice"
                    handleAddNew={handleAddNew}
                    handleSelection={handleCategoryCheckbox}
                    search={searchCategory}
                    setSearch={setSearchCategory}
                    apiUrl={apiRoutes.GET_INVOICE_LIST}
                    refetch={categoryListRefetch}
                    hideEdit={true}
                    initialFormValue={companyFormInitVal}
                    permission={canEditInvoice}
                    selectedRowId={categoryData?.invoiceID || null}
                    rowCount={invoiceData?.data?.count || 0}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    getRowClassName={getRowClassName}
                  />
                </div>
              </Box>
            </Box>
          </SplitContainer>
          <SplitContainer
            id="tour-invoice-form-pane"
            style={{
              backgroundColor: "transparent",
              boxShadow: "none",
            }}
            sx={{
              cursor: addNewCategory ? "auto" : "not-allowed",
              width: { xs: "100%", md: "35%" },
            }}
          >
            <InvoiceForm
              categoryListRefetch={categoryListRefetch}
              setCompanyForm={setCompanyForm}
              categoryData={categoryData}
              setCategoryData={setCategoryData}
              setAddNewCategory={setAddNewCategory}
              addNewCategory={addNewCategory}
              setSelectedCategories={setSelectedCategories}
              permission={canEditInvoice}
              readOnly={!canEditInvoice}
            />
          </SplitContainer>
        </ParentContainer>
      </BoxContainer>
    </ParentPageWrapper>
  );
};
